import axios from 'axios';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password, otp = null) => {
    const payload = { email, password };
    if (otp) payload.otp = otp;
    const response = await api.post('/api/login', payload);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  },

  register: async (email, password, firstname, surname, nickname) => {
    const response = await api.post('/api/register', {
      email,
      password,
      firstname,
      surname,
      nickname,
    });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
  },

  getCurrentUser: async () => {
    const response = await api.get('/api/me');
    return response.data;
  },

  isAuthenticated: () => {
    return localStorage.getItem('token') !== null;
  },
};

// Club API
export const clubAPI = {
  createClub: async (clubData) => {
    const response = await api.post('/api/clubs', clubData);
    return response.data;
  },

  listClubs: async () => {
    const response = await api.get('/api/clubs');
    return response.data;
  },

  bookieCallback: async (callbackData) => {
    const response = await api.post('/api/clubs/bookie-callback', callbackData);
    return response.data;
  },

  getClubEditData: async (clubId) => {
    const response = await api.get(`/api/club/${clubId}/edit`);
    return response.data;
  },

  updateClub: async (clubId, data) => {
    const response = await api.put(`/api/club/${clubId}/edit`, data);
    return response.data;
  },

  togglePauseClub: async (clubId) => {
    const response = await api.post(`/api/club/${clubId}/pause`);
    return response.data;
  },

  startClub: async (clubId) => {
    const response = await api.post(`/api/club/${clubId}/start`);
    return response.data;
  },

  // Member management
  suspendMember: async (clubId, userId) => {
    const response = await api.post(`/api/clubs/${clubId}/members/${userId}/suspend`);
    return response.data;
  },

  activateMember: async (clubId, userId) => {
    const response = await api.post(`/api/clubs/${clubId}/members/${userId}/activate`);
    return response.data;
  },

  ejectMember: async (clubId, userId) => {
    const response = await api.post(`/api/clubs/${clubId}/members/${userId}/eject`);
    return response.data;
  },

  setMemberBetting: async (clubId, userId, betting) => {
    const response = await api.post(`/api/clubs/${clubId}/members/${userId}/set-betting`, { betting });
    return response.data;
  },

  fundClub: async (clubId, amount, walletType = 'kitty') => {
    const response = await api.post(`/api/club/${clubId}/fund`, { amount, walletType });
    return response.data;
  },

  reloadBettingAccount: async (clubId) => {
    const response = await api.post(`/api/club/${clubId}/reload-betting`);
    return response.data;
  },

  transferFunds: async (clubId, amount, from, to) => {
    const response = await api.post(`/api/club/${clubId}/transfer`, { amount, from, to });
    return response.data;
  },

  leaveClub: async (clubId) => {
    const response = await api.post(`/api/clubs/${clubId}/leave`);
    return response.data;
  },

  getPendingBets: async (clubId) => {
    const response = await api.get(`/api/club/${clubId}/pending-bets`);
    return response.data;
  },

  getGoals: async (clubId) => {
    const response = await api.get(`/api/club/${clubId}/goals`);
    return response.data;
  },

  createGoal: async (clubId, goalData) => {
    const response = await api.post(`/api/club/${clubId}/goals`, goalData);
    return response.data;
  },

  getActiveGoal: async (clubId) => {
    const response = await api.get(`/api/club/${clubId}/goals/active`);
    return response.data;
  },

  cancelGoal: async (goalId) => {
    const response = await api.post(`/api/goal/${goalId}/cancel`);
    return response.data;
  },

  achieveGoal: async (goalId) => {
    const response = await api.post(`/api/goal/${goalId}/achieve`);
    return response.data;
  },

  getClubSubscription: async (clubId) => {
    const response = await api.get(`/api/club/${clubId}/subscription`);
    return response.data;
  },

  broadcastEmail: async (clubId, subject, message) => {
    const response = await api.post(`/api/club/${clubId}/broadcast-email`, { subject, message });
    return response.data;
  },

  broadcastPush: async (clubId, message) => {
    const response = await api.post(`/api/club/${clubId}/broadcast-push`, { message });
    return response.data;
  },

  broadcastSms: async (clubId, message) => {
    const response = await api.post(`/api/club/${clubId}/broadcast-sms`, { message });
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getDashboard: async () => {
    const response = await api.get('/api/dashboard');
    return response.data;
  },

  getClubDashboard: async (clubId) => {
    const response = await api.get(`/api/dashboard/${clubId}`);
    return response.data;
  },
};

// Bookie API
export const bookieAPI = {
  getRegistrationLink: async (clubId, action = null) => {
    let url = `/api/register/link?clubId=${clubId}`;
    if (action) url += `&action=${action}`;
    const response = await api.get(url);
    return response.data;
  },

  getBookies: async (siteId = null) => {
    const url = siteId
      ? `/api/bookies?_si=${siteId}`
      : '/api/bookies';
    const response = await axios.get(`${API_URL}${url}`);
    return response.data;
  },

  getBookieAccounts: async () => {
    const response = await api.get('/api/user/bookie-accounts');
    return response.data;
  },

  unlinkBookieAccount: async (accountId) => {
    const response = await api.delete(`/api/user/bookie-accounts/${accountId}`);
    return response.data;
  },

  getBetLaunchUrl: async (clubId, walletType) => {
    const response = await api.get(`/api/bet/launch?clubId=${clubId}&walletType=${walletType}`);
    return response.data;
  },

  getPersonalBetLaunchUrl: async (brokerId = 'grsbet') => {
    const response = await api.get(`/api/bet/launch?walletType=personal&brokerId=${brokerId}`);
    return response.data;
  },

  getBookieBalance: async (clubId) => {
    const response = await api.get(`/api/club/${clubId}/bookie-balance`);
    return response.data;
  },
};

// Withdrawal API
export const withdrawalAPI = {
  create: async (clubId, amount, comment = '') => {
    const response = await api.post(`/api/club/${clubId}/withdrawal`, { amount, comment });
    return response.data;
  },

  preview: async (clubId, amount) => {
    const response = await api.post(`/api/club/${clubId}/withdrawal/preview`, { amount });
    return response.data;
  },

  current: async (clubId) => {
    const response = await api.get(`/api/club/${clubId}/withdrawal/current`);
    return response.data;
  },

  list: async (clubId) => {
    const response = await api.get(`/api/club/${clubId}/withdrawals`);
    return response.data;
  },

  process: async (withdrawalId) => {
    const response = await api.post(`/api/withdrawal/${withdrawalId}/process`);
    return response.data;
  },

  cancel: async (withdrawalId) => {
    const response = await api.post(`/api/withdrawal/${withdrawalId}/cancel`);
    return response.data;
  },

  respond: async (withdrawalId, approve) => {
    const response = await api.post(`/api/withdrawal/${withdrawalId}/respond`, { approve });
    return response.data;
  },
};

// Invitation API
export const invitationAPI = {
  send: async (clubId, emails, message = '') => {
    const response = await api.post(`/api/club/${clubId}/invite`, { emails, message });
    return response.data;
  },

  generateLink: async (clubId) => {
    const response = await api.get(`/api/club/${clubId}/invite-link`);
    return response.data;
  },

  resend: async (inviteId) => {
    const response = await api.post(`/api/invitation/${inviteId}/resend`);
    return response.data;
  },

  getDetails: async (token) => {
    const response = await api.get(`/api/invitation/${token}/details`);
    return response.data;
  },

  accept: async (token) => {
    const response = await api.post(`/api/invitation/${token}/accept`);
    return response.data;
  },

  list: async (clubId) => {
    const response = await api.get(`/api/club/${clubId}/invitations`);
    return response.data;
  },

  accept: async (token) => {
    const response = await api.post(`/api/invitation/${token}/accept`);
    return response.data;
  },
};

// Vault API (PCI Vault card storage)
export const vaultAPI = {
  createSession: async (clubId) => {
    const response = await api.post(`/api/club/${clubId}/vault/session`);
    return response.data;
  },

  confirmToken: async (clubId, tokenData) => {
    const response = await api.post(`/api/club/${clubId}/vault/confirm`, tokenData);
    return response.data;
  },

  getStatus: async (clubId) => {
    const response = await api.get(`/api/club/${clubId}/vault/status`);
    return response.data;
  },

  getConfig: async (clubId) => {
    const response = await api.get(`/api/club/${clubId}/vault/config`);
    return response.data;
  },
};

// Notification API
export const notificationAPI = {
  list: async (limit = 20, unreadOnly = false) => {
    const params = `limit=${limit}${unreadOnly ? '&unread=1' : ''}`;
    const response = await api.get(`/api/notifications?${params}`);
    return response.data;
  },

  markRead: async (ids = []) => {
    const response = await api.post('/api/notifications/read', { ids });
    return response.data;
  },

  getCount: async () => {
    const response = await api.get('/api/notifications/count');
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  getDashboard: async () => {
    const response = await api.get('/api/admin/dashboard');
    return response.data;
  },

  getUsers: async (search = '', limit = 50, offset = 0) => {
    const response = await api.get(`/api/admin/users?search=${search}&limit=${limit}&offset=${offset}`);
    return response.data;
  },

  getUserDetail: async (userId) => {
    const response = await api.get(`/api/admin/users/${userId}`);
    return response.data;
  },

  getClubs: async (search = '', limit = 50, offset = 0) => {
    const response = await api.get(`/api/admin/clubs?search=${search}&limit=${limit}&offset=${offset}`);
    return response.data;
  },

  getClubDetail: async (clubId) => {
    const response = await api.get(`/api/admin/clubs/${clubId}`);
    return response.data;
  },

  getBets: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/api/admin/bets?${query}`);
    return response.data;
  },

  getTransactions: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const response = await api.get(`/api/admin/transactions?${query}`);
    return response.data;
  },

  getWebhooks: async (limit = 50) => {
    const response = await api.get(`/api/admin/webhooks?limit=${limit}`);
    return response.data;
  },

  getSubscriptions: async (state = '') => {
    const response = await api.get(`/api/admin/subscriptions${state ? '?state=' + state : ''}`);
    return response.data;
  },
};

// User Settings API
export const userSettingsAPI = {
  // Personal Details
  getPersonalDetails: async () => {
    const response = await api.get('/api/user/profile');
    return response.data;
  },

  updatePersonalDetails: async (data) => {
    const response = await api.put('/api/user/profile', data);
    return response.data;
  },

  // Security
  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/api/user/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  enable2FA: async () => {
    const response = await api.post('/api/user/2fa/enable');
    return response.data;
  },

  disable2FA: async () => {
    const response = await api.post('/api/user/2fa/disable');
    return response.data;
  },

  verify2FA: async (token) => {
    const response = await api.post('/api/user/2fa/verify', { token });
    return response.data;
  },

  // Profile Image
  uploadProfileImage: async (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    const response = await api.post('/api/user/profile-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteProfileImage: async () => {
    const response = await api.delete('/api/user/profile-image');
    return response.data;
  },

  // Notification Settings
  getNotificationSettings: async () => {
    const response = await api.get('/api/user/settings/notifications');
    return response.data;
  },

  updateNotificationSettings: async (settings) => {
    const response = await api.put('/api/user/settings/notifications', settings);
    return response.data;
  },

  // Subscriptions
  getSubscriptions: async () => {
    const response = await api.get('/api/user/subscriptions');
    return response.data;
  },

  cancelSubscription: async (subscriptionId) => {
    const response = await api.delete(`/api/user/subscriptions/${subscriptionId}`);
    return response.data;
  },

  // Referrals
  getReferralLink: async () => {
    const response = await api.get('/api/user/referral-link');
    return response.data;
  },

  getReferralStats: async () => {
    const response = await api.get('/api/user/referral-stats');
    return response.data;
  },

  // Club Management
  leaveClub: async (clubId) => {
    const response = await api.post(`/api/clubs/${clubId}/leave`);
    return response.data;
  },

  // Active Club
  setActiveClub: async (clubId) => {
    const response = await api.put('/api/user/active-club', { clubId });
    return response.data;
  },

  // Achievements
  getAchievements: async () => {
    const response = await api.get('/api/user/achievements');
    return response.data;
  },
};

// Wall API
export const wallAPI = {
  getWallMessages: async (clubId, page = 1, limit = 20) => {
    const response = await api.get(`/api/clubs/${clubId}/wall?page=${page}&limit=${limit}`);
    return response.data;
  },

  createWallMessage: async (clubId, message) => {
    const response = await api.post(`/api/clubs/${clubId}/wall`, { message });
    return response.data;
  },

  getComments: async (wallMessageId) => {
    const response = await api.get(`/api/wall/${wallMessageId}/comments`);
    return response.data;
  },

  createComment: async (wallMessageId, comment) => {
    const response = await api.post(`/api/wall/${wallMessageId}/comments`, { comment });
    return response.data;
  },

  createCommentWithImage: async (wallMessageId, formData) => {
    const response = await api.post(`/api/wall/${wallMessageId}/comments/with-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  uploadImage: async (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    const response = await api.post('/api/wall/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  parseOgp: async (url) => {
    const response = await api.get(`/api/ogp?url=${encodeURIComponent(url)}`);
    return response.data;
  },
};

// Lottery API
export const lotteryAPI = {
  getGames: async () => {
    const response = await api.get('/api/lottery/games');
    return response.data;
  },
  getDraws: async (clubId, gameCode = null) => {
    let url = `/api/clubs/${clubId}/lottery/draws`;
    if (gameCode) url += `?gameCode=${gameCode}`;
    const response = await api.get(url);
    return response.data;
  },
  createDraw: async (clubId, data) => {
    const response = await api.post(`/api/clubs/${clubId}/lottery/draws`, data);
    return response.data;
  },
  getDrawDetails: async (clubId, drawId) => {
    const response = await api.get(`/api/clubs/${clubId}/lottery/draws/${drawId}`);
    return response.data;
  },
  buyShares: async (clubId, drawId, shares, drawData = {}) => {
    const response = await api.post(`/api/clubs/${clubId}/lottery/draws/${drawId}/shares`, { shares, ...drawData });
    return response.data;
  },
  closeDraw: async (clubId, drawId) => {
    const response = await api.post(`/api/clubs/${clubId}/lottery/draws/${drawId}/close`);
    return response.data;
  },
  getMyShares: async (clubId) => {
    const response = await api.get(`/api/clubs/${clubId}/lottery/shares`);
    return response.data;
  },
  getHistory: async (clubId) => {
    const response = await api.get(`/api/clubs/${clubId}/lottery/history`);
    return response.data;
  },
  getPublicDraws: async () => {
    const response = await api.get('/api/lottery/public/draws');
    return response.data;
  },
  getPublicDrawDetails: async (drawId) => {
    const response = await api.get(`/api/lottery/public/draws/${drawId}`);
    return response.data;
  },
  buyPublicShares: async (drawId, shares) => {
    const response = await api.post(`/api/lottery/public/draws/${drawId}/shares`, { shares });
    return response.data;
  },
  getPublicShares: async () => {
    const response = await api.get('/api/lottery/public/shares');
    return response.data;
  },
  getPublicHistory: async () => {
    const response = await api.get('/api/lottery/public/history');
    return response.data;
  },
};

// Crypto API
export const cryptoAPI = {
  getAssets: async () => {
    const response = await api.get('/api/crypto/assets');
    return response.data;
  },
  getPrice: async (productId) => {
    const response = await api.get(`/api/crypto/price/${productId}`);
    return response.data;
  },
  getHoldings: async (clubId) => {
    const response = await api.get(`/api/clubs/${clubId}/crypto/holdings`);
    return response.data;
  },
  getMemberShare: async (clubId) => {
    const response = await api.get(`/api/clubs/${clubId}/crypto/member-share`);
    return response.data;
  },
  getAllShares: async (clubId) => {
    const response = await api.get(`/api/clubs/${clubId}/crypto/all-shares`);
    return response.data;
  },
  createProposal: async (clubId, data) => {
    const response = await api.post(`/api/clubs/${clubId}/crypto/proposals`, data);
    return response.data;
  },
  getProposals: async (clubId) => {
    const response = await api.get(`/api/clubs/${clubId}/crypto/proposals`);
    return response.data;
  },
  voteOnProposal: async (clubId, proposalId, vote) => {
    const response = await api.post(`/api/clubs/${clubId}/crypto/proposals/${proposalId}/vote`, { vote });
    return response.data;
  },
  getTransactions: async (clubId) => {
    const response = await api.get(`/api/clubs/${clubId}/crypto/transactions`);
    return response.data;
  },
};

export default api;
